import React, { useState, useEffect, useRef } from "react";
import api from "../api/axios";
import { trackLogin, trackSignUp } from "../utils/analytics";

const MAHARASHTRA_CITIES = [
  "Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Solapur",
  "Amravati", "Kolhapur", "Sangli", "Satara", "Ratnagiri", "Ahmednagar",
  "Jalgaon", "Akola", "Latur", "Nanded", "Thane", "Kalyan", "Vasai",
  "Palghar", "Raigad", "Sindhudurg", "Beed", "Osmanabad", "Hingoli",
  "Washim", "Yavatmal", "Wardha", "Gadchiroli", "Gondia", "Bhandara",
  "Chandrapur", "Nandurbar", "Dhule", "Buldhana", "Other"
];

const INDIA_STATES = [
  { name: "Maharashtra", available: true },
  { name: "Delhi", available: false },
  { name: "Karnataka", available: false },
  { name: "Tamil Nadu", available: false },
  { name: "Uttar Pradesh", available: false },
  { name: "Gujarat", available: false },
  { name: "Rajasthan", available: false },
  { name: "West Bengal", available: false },
  { name: "Telangana", available: false },
  { name: "Kerala", available: false },
  { name: "Madhya Pradesh", available: false },
  { name: "Bihar", available: false },
  { name: "Punjab", available: false },
  { name: "Haryana", available: false },
  { name: "Other", available: false },
];

const VALID_EMAIL_DOMAINS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Shared UI ─────────────────────────────────────────────────────────────────

const ErrorMsg = ({ message }) =>
  message ? <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#c0392b", marginTop: "4px" }}>⚠ {message}</p> : null;

const Label = ({ children, required }) => (
  <label style={{ display: "block", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#5a3a1a", marginBottom: "5px", letterSpacing: "0.04em" }}>
    {children}{required && <span style={{ color: "#e07b39", marginLeft: "2px" }}>*</span>}
  </label>
);

const inputBase = (focused, error) => ({
  width: "100%", padding: "10px 12px", borderRadius: "9px",
  fontFamily: "Georgia, serif", fontSize: "13px", color: "#1a1a1a",
  background: "#fffaf4", outline: "none", boxSizing: "border-box",
  border: `1.5px solid ${focused ? "#e07b39" : error ? "#e57373" : "#e8d5b8"}`,
  transition: "border-color 0.2s",
});

const TextField = ({ label, name, type = "text", placeholder, value, onChange, error, required, hint, maxLength, autoComplete, showToggle }) => {
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const inputType = showToggle ? (visible ? "text" : "password") : type;
  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      <div style={{ position: "relative" }}>
        <input name={name} type={inputType} placeholder={placeholder} value={value}
          onChange={onChange} autoComplete={autoComplete} maxLength={maxLength}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ ...inputBase(focused, error), paddingRight: showToggle ? "38px" : "12px" }} />
        {showToggle && (
          <button type="button" onClick={() => setVisible(v => !v)} tabIndex={-1}
            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#a07850", padding: 0 }}>
            {visible ? "🙈" : "👁️"}
          </button>
        )}
      </div>
      {hint && !error && <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#bbb", marginTop: "3px" }}>{hint}</p>}
      <ErrorMsg message={error} />
    </div>
  );
};

const SelectField = ({ label, name, value, onChange, error, required, children }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      <select name={name} value={value} onChange={onChange}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ ...inputBase(focused, error), appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23a07850' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: "32px", cursor: "pointer" }}>
        {children}
      </select>
      <ErrorMsg message={error} />
    </div>
  );
};

const StrengthBar = ({ password }) => {
  if (!password) return null;
  const score = [password.length >= 8, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;
  const colors = ["", "#e57373", "#ffb74d", "#81c784", "#3a8a68"];
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  return (
    <div style={{ marginTop: "4px" }}>
      <div style={{ display: "flex", gap: "3px", marginBottom: "2px" }}>
        {[1, 2, 3, 4].map(i => <div key={i} style={{ flex: 1, height: "3px", borderRadius: "999px", background: i <= score ? colors[score] : "#e8d5b8", transition: "background 0.3s" }} />)}
      </div>
      {score > 0 && <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: colors[score] }}>{labels[score]}</p>}
    </div>
  );
};

const CandleParticles = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    const pts = Array.from({ length: 25 }, () => ({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, r: Math.random() * 1.6 + 0.3, sp: Math.random() * 0.35 + 0.08, op: Math.random() * 0.4 + 0.08, dr: (Math.random() - 0.5) * 0.25 }));
    let id;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => { ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(255,200,110,${p.op})`; ctx.fill(); p.y -= p.sp; p.x += p.dr; if (p.y < -5) { p.y = canvas.height + 5; p.x = Math.random() * canvas.width; } });
      id = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex: 1 }} />;
};

const Logo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px" }}>
    <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "linear-gradient(135deg, #ea8a3a, #c96a18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="15" height="17" viewBox="0 0 18 20" fill="none">
        <path d="M9 0C9 0 14 6 14 10.5C14 13.538 11.761 16 9 16C6.239 16 4 13.538 4 10.5C4 6 9 0 9 0Z" fill="white" opacity="0.9" />
        <path d="M9 8C9 8 11.5 11 11.5 13C11.5 14.38 10.38 15.5 9 15.5C7.62 15.5 6.5 14.38 6.5 13C6.5 11 9 8 9 8Z" fill="#f5e6cc" opacity="0.75" />
      </svg>
    </div>
    <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#7a4010", letterSpacing: "0.1em", textTransform: "uppercase" }}>SJ Aroma Candles</span>
  </div>
);

const StepIndicator = ({ step }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", marginBottom: "24px" }}>
    {["Account", "Address"].map((s, i) => (
      <div key={s} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <div style={{ width: "22px", height: "22px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", background: step > i + 1 ? "#3a8a68" : step === i + 1 ? "#e07b39" : "#f0ebe3", color: step >= i + 1 ? "#fff" : "#bbb" }}>
            {step > i + 1 ? "✓" : i + 1}
          </div>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: step === i + 1 ? "#1a1a1a" : "#bbb", fontWeight: step === i + 1 ? "700" : "400" }}>{s}</span>
        </div>
        {i < 1 && <div style={{ width: "32px", height: "1.5px", background: step > 1 ? "#3a8a68" : "#e8dfd0" }} />}
      </div>
    ))}
  </div>
);

// ── LOGIN FORM ────────────────────────────────────────────────────────────────

const LoginForm = ({ onSwitch }) => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => { setForm({ ...form, [e.target.name]: e.target.value }); setErrors({ ...errors, [e.target.name]: "" }); setApiError(""); };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = {};
    if (!form.username.trim()) errs.username = "Username or email is required.";
    if (!form.password) errs.password = "Password is required.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const res = await api.post("token/", { username: form.username, password: form.password });
      localStorage.setItem("token", res.data.access);
      trackLogin("email");
      if (rememberMe) localStorage.setItem("refresh_token", res.data.refresh);
      window.location.href = "/";
    } catch (err) {
      setApiError(err.response?.status === 401 ? "Invalid username or password." : "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Logo />
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "700", color: "#1a0a00", marginBottom: "4px" }}>Welcome back 👋</h2>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#a07850", marginBottom: "24px" }}>Sign in to your account to continue</p>
      {apiError && <div style={{ background: "#fff0f0", border: "1px solid #f5c6cb", color: "#a94442", borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", fontFamily: "Georgia, serif", fontSize: "12px" }}>⚠️ {apiError}</div>}
      <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <TextField label="Username or Email" name="username" placeholder="alex@gmail.com" value={form.username} onChange={handleChange} error={errors.username} required autoComplete="username" />
        <div>
          <div style={{ marginBottom: "5px" }}>
            <Label required>Password</Label>
          </div>
          <TextField name="password" placeholder="Enter your password" value={form.password} onChange={handleChange} error={errors.password} autoComplete="current-password" showToggle />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
          <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} style={{ accentColor: "#ea8a3a", width: "14px", height: "14px" }} />
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#7a5c3a" }}>Remember me for 30 days</span>
        </label>
        <button type="submit" disabled={loading}
          style={{ width: "100%", padding: "13px", borderRadius: "11px", background: loading ? "#e0a870" : "linear-gradient(135deg, #ea8a3a, #c96a18)", color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", letterSpacing: "0.06em", boxShadow: "0 4px 18px rgba(201,106,24,.3)" }}>
          {loading ? "Signing in..." : "Sign In →"}
        </button>
      </form>
      <div style={{ margin: "20px 0", display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ flex: 1, height: "1px", background: "#f0dfc0" }} /> <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#ccc" }}>or</span> <div style={{ flex: 1, height: "1px", background: "#f0dfc0" }} />
      </div>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", textAlign: "center" }}>
        Don't have an account?{" "}
        <button onClick={onSwitch} style={{ background: "none", border: "none", color: "#ea8a3a", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Create account</button>
      </p>
    </div>
  );
};

// ── REGISTER STEP 1 ───────────────────────────────────────────────────────────

const AccountStep = ({ data, onChange, onNext, backendErrors, setBackendErrors }) => {
  const [errors, setErrors] = useState({});

  // Merge backend errors into display
  const allErrors = { ...errors, ...backendErrors };

  const validate = () => {
    const errs = {};
    if (!data.username.trim()) errs.username = "Username is required.";
    else if (data.username.trim().length > 20) errs.username = "Max 20 characters.";
    if (!data.email.trim()) errs.email = "Email is required.";
    else if (!VALID_EMAIL_DOMAINS.test(data.email)) errs.email = "Use a valid provider: Gmail, Yahoo, Outlook, etc.";
    if (!data.password)
    errs.password = "Password is required.";
    else if (data.password.length < 6)
    errs.password = "Minimum 6 characters required.";
    if (!data.confirmPassword) errs.confirmPassword = "Please confirm your password.";
    else if (data.password !== data.confirmPassword) errs.confirmPassword = "Passwords do not match.";
    if (!data.contact.trim()) errs.contact = "Contact number is required.";
    else if (!/^\d{10}$/.test(data.contact)) errs.contact = "Must be exactly 10 digits.";
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    onNext();
  };

  const handle = e => {
    onChange(e.target.name, e.target.value);
    setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    setBackendErrors(prev => ({ ...prev, [e.target.name]: "" }));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div>
          <TextField label="Username" name="username" placeholder="CandleLover99" value={data.username}
            onChange={handle} error={allErrors.username} required autoComplete="username"
            hint={`${data.username.length}/20`} maxLength={20} />
        </div>
        <TextField label="Email" name="email" type="email" placeholder="alex@gmail.com" value={data.email}
          onChange={handle} error={allErrors.email} required autoComplete="email" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div>
          <TextField label="Password" name="password" placeholder="Min 8, max 10 chars" value={data.password}
            onChange={handle} error={allErrors.password} required autoComplete="new-password" showToggle hint="Min 8 chars · include a special character (@#!)" />
          <StrengthBar password={data.password} />
        </div>
        <div>
          <TextField label="Confirm Password" name="confirmPassword" placeholder="Re-enter password" value={data.confirmPassword}
            onChange={handle} error={allErrors.confirmPassword} required autoComplete="new-password" showToggle />
          {data.confirmPassword && data.password === data.confirmPassword && !allErrors.confirmPassword && (
            <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#3a8a68", marginTop: "3px" }}>✓ Passwords match</p>
          )}
        </div>
      </div>

      <div>
        <Label required>Contact Number</Label>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", fontFamily: "Georgia, serif", fontSize: "13px", color: "#a07850" }}>+91</span>
          <input name="contact" type="tel" placeholder="10-digit mobile number" value={data.contact}
            onChange={e => { const val = e.target.value.replace(/\D/g, "").slice(0, 10); onChange("contact", val); setErrors(prev => ({ ...prev, contact: "" })); setBackendErrors(prev => ({ ...prev, contact_number: "" })); }}
            style={{ ...inputBase(false, allErrors.contact || allErrors.contact_number), paddingLeft: "40px" }} />
        </div>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: (allErrors.contact || allErrors.contact_number) ? "#c0392b" : "#bbb", marginTop: "3px" }}>
          {allErrors.contact ? `⚠ ${allErrors.contact}` : allErrors.contact_number ? `⚠ ${allErrors.contact_number}` : `${data.contact.length}/10 digits`}
        </p>
      </div>

      <button type="button" onClick={handleNext}
        style={{ width: "100%", padding: "12px", borderRadius: "11px", background: "linear-gradient(135deg, #ea8a3a, #c96a18)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", letterSpacing: "0.06em", boxShadow: "0 4px 16px rgba(201,106,24,.28)" }}>
        Continue to Address →
      </button>
    </div>
  );
};

// ── REGISTER STEP 2 ───────────────────────────────────────────────────────────

const AddressStep = ({ data, onChange, onBack, onSubmit, loading, apiError }) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!data.full_name.trim()) errs.full_name = "Full name is required.";
    if (!data.address_line_1.trim()) errs.address_line_1 = "Address line 1 is required.";
    if (!data.city) errs.city = "City is required.";
    if (!data.state) errs.state = "State is required.";
    else if (data.state !== "Maharashtra") errs.state = "Currently only Maharashtra is serviceable.";
    if (!data.postal_code.trim()) errs.postal_code = "Postal code is required.";
    else if (!/^\d{6}$/.test(data.postal_code)) errs.postal_code = "Must be 6 digits.";
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit();
  };

  const handle = e => { onChange(e.target.name, e.target.value); setErrors(prev => ({ ...prev, [e.target.name]: "" })); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {apiError && <div style={{ background: "#fff0f0", border: "1px solid #f5c6cb", color: "#a94442", borderRadius: "10px", padding: "10px 14px", fontFamily: "Georgia, serif", fontSize: "12px" }}>⚠️ {apiError}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <TextField label="Full Name" name="full_name" placeholder="Priya Jha" value={data.full_name} onChange={handle} error={errors.full_name} required />
        <div>
          <Label>Phone</Label>
          <input value={`+91 ${data.phone}`} readOnly style={{ ...inputBase(false, false), background: "#f5f0e8", color: "#888", cursor: "not-allowed" }} />
          <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#bbb", marginTop: "3px" }}>From account details</p>
        </div>
      </div>

      <TextField label="Address Line 1" name="address_line_1" placeholder="House/Flat No., Street, Area" value={data.address_line_1} onChange={handle} error={errors.address_line_1} required />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <TextField label="Address Line 2" name="address_line_2" placeholder="Apartment, Building (optional)" value={data.address_line_2} onChange={handle} />
        <TextField label="Landmark" name="landmark" placeholder="Near school, temple, etc." value={data.landmark} onChange={handle} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <SelectField label="State" name="state" value={data.state} onChange={handle} error={errors.state} required>
          <option value="">Select State</option>
          {INDIA_STATES.map(s => (
            <option key={s.name} value={s.name} disabled={!s.available}>
              {s.name}{!s.available ? " (Coming Soon)" : ""}
            </option>
          ))}
        </SelectField>

        <div>
          <Label required>City</Label>
          {data.state === "Maharashtra" ? (
            <select name="city" value={data.city} onChange={handle}
              style={{ ...inputBase(false, errors.city), appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23a07850' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: "32px", cursor: "pointer" }}>
              <option value="">Select City</option>
              {MAHARASHTRA_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input value={data.city} onChange={e => { onChange("city", e.target.value); setErrors(prev => ({ ...prev, city: "" })); }}
              placeholder="Enter city" style={inputBase(false, errors.city)} />
          )}
          <ErrorMsg message={errors.city} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <TextField label="Postal Code" name="postal_code" placeholder="400001" value={data.postal_code}
          onChange={e => { const val = e.target.value.replace(/\D/g, "").slice(0, 6); onChange("postal_code", val); setErrors(prev => ({ ...prev, postal_code: "" })); }}
          error={errors.postal_code} required hint="6-digit PIN code" />
        <div>
          <Label required>Country</Label>
          <input value="India 🇮🇳" readOnly style={{ ...inputBase(false, false), background: "#f5f0e8", color: "#888", cursor: "not-allowed" }} />
          <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#bbb", marginTop: "3px" }}>Shipping available in India only</p>
        </div>
      </div>

      {data.state && data.state !== "Maharashtra" && (
        <div style={{ background: "#fff8ec", border: "1px solid #f5d9a0", borderRadius: "10px", padding: "10px 14px" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#c47a3a" }}>
            🚚 Currently we ship only within <strong>Maharashtra</strong>. Other states coming soon!
          </p>
        </div>
      )}

      <div style={{ display: "flex", gap: "10px" }}>
        <button type="button" onClick={onBack}
          style={{ flex: "0 0 auto", padding: "12px 20px", borderRadius: "11px", background: "transparent", border: "1.5px solid #e8d5b8", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px" }}>
          ← Back
        </button>
        <button type="button" onClick={handleSubmit} disabled={loading}
          style={{ flex: 1, padding: "12px", borderRadius: "11px", background: loading ? "#e0a870" : "linear-gradient(135deg, #ea8a3a, #c96a18)", color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", letterSpacing: "0.06em", boxShadow: "0 4px 16px rgba(201,106,24,.28)" }}>
          {loading ? "Creating account..." : "Create Account →"}
        </button>
      </div>
    </div>
  );
};

// ── REGISTER MAIN ─────────────────────────────────────────────────────────────

const RegisterForm = ({ onSwitch }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [backendErrors, setBackendErrors] = useState({});  // errors that map to step 1 fields
  const [success, setSuccess] = useState(false);

  const [account, setAccount] = useState({ username: "", email: "", password: "", confirmPassword: "", contact: "" });
  const [address, setAddress] = useState({ full_name: "", phone: "", address_line_1: "", address_line_2: "", landmark: "", city: "", state: "Maharashtra", postal_code: "", country: "India" });

  const handleAccountChange = (name, val) => setAccount(prev => ({ ...prev, [name]: val }));
  const handleAddressChange = (name, val) => setAddress(prev => ({ ...prev, [name]: val }));

  const handleSubmit = async () => {
    setLoading(true);
    setApiError("");
    setBackendErrors({});
    try {
      await api.post("users/register/", {
        username: account.username,
        email: account.email,
        password: account.password,
        contact_number: account.contact,
        full_name: address.full_name,
        address_line_1: address.address_line_1,
        address_line_2: address.address_line_2,
        landmark: address.landmark,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country,
      });
      trackSignUp("email");
      setSuccess(true);
    } catch (error) {
      if (error.response?.data) {
        const data = error.response.data;
        const key = Object.keys(data)[0];
        const msg = Array.isArray(data[key]) ? data[key][0] : data[key];

        // If error is about a step-1 field → go back to step 1 and show inline
        if (["username", "email", "contact_number"].includes(key)) {
          setBackendErrors({ [key]: msg });
          setStep(1);
        } else {
          setApiError(`${key}: ${msg}`);
        }
      } else {
        setApiError("Registration failed. Please try again.");
      }
    } finally { setLoading(false); }
  };

  if (success) return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ fontSize: "52px", marginBottom: "16px" }}>🕯️</div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a0a00", marginBottom: "8px" }}>Welcome to SJ Aroma!</h2>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#a07850", marginBottom: "24px" }}>Your account has been created. You can now sign in.</p>
      <button onClick={onSwitch} style={{ padding: "12px 32px", borderRadius: "11px", background: "linear-gradient(135deg, #ea8a3a, #c96a18)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", boxShadow: "0 4px 16px rgba(201,106,24,.3)" }}>
        Sign In →
      </button>
    </div>
  );

  return (
    <div>
      <Logo />
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a0a00", marginBottom: "4px" }}>Create your account</h2>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#a07850", marginBottom: "20px" }}>Join the glow and start shopping</p>
      <StepIndicator step={step} />
      {step === 1 && (
        <AccountStep data={account} onChange={handleAccountChange}
          onNext={() => { setAddress(prev => ({ ...prev, full_name: account.username, phone: account.contact })); setStep(2); }}
          backendErrors={backendErrors} setBackendErrors={setBackendErrors} />
      )}
      {step === 2 && (
        <AddressStep data={{ ...address, phone: account.contact }} onChange={handleAddressChange}
          onBack={() => setStep(1)} onSubmit={handleSubmit} loading={loading} apiError={apiError} />
      )}
      <div style={{ margin: "18px 0", display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ flex: 1, height: "1px", background: "#f0dfc0" }} /> <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#ccc" }}>or</span> <div style={{ flex: 1, height: "1px", background: "#f0dfc0" }} />
      </div>
      <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", textAlign: "center" }}>
        Already have an account?{" "}
        <button onClick={onSwitch} style={{ background: "none", border: "none", color: "#ea8a3a", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Sign in</button>
      </p>
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const Login = () => {
  const [mode, setMode] = useState("login");
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", background: "radial-gradient(ellipse at 65% 25%, #3d1a00 0%, #1a0a00 45%, #0d0500 100%)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "fixed", top: "5%", right: "12%", width: "280px", height: "360px", borderRadius: "50%", background: "radial-gradient(ellipse, rgba(220,130,30,.22) 0%, transparent 70%)", filter: "blur(32px)", zIndex: 0, pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: "8%", left: "6%", width: "200px", height: "200px", borderRadius: "50%", background: "radial-gradient(circle, rgba(200,100,20,.14) 0%, transparent 70%)", filter: "blur(22px)", zIndex: 0, pointerEvents: "none" }} />
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.42)", zIndex: 0, pointerEvents: "none" }} />
      <CandleParticles />
      <div style={{ position: "relative", zIndex: 10, width: "100%", maxWidth: mode === "register" ? "580px" : "420px", background: "#fff9f2", borderRadius: "20px", padding: "36px 36px 30px", boxShadow: "0 32px 80px rgba(80,30,0,.35)", transition: "max-width 0.3s ease", overflowY: "auto", maxHeight: "93vh" }}>
        {mode === "login" ? <LoginForm onSwitch={() => setMode("register")} /> : <RegisterForm onSwitch={() => setMode("login")} />}
      </div>
      <div style={{ position: "fixed", bottom: "14px", left: 0, right: 0, display: "flex", justifyContent: "center", gap: "24px", zIndex: 10, pointerEvents: "none" }}>
        {["© 2025 SJ AROMA CANDLES", "HELP CENTER", "COOKIE SETTINGS"].map(item => (
          <span key={item} style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "rgba(255,200,140,.4)", letterSpacing: "0.08em", pointerEvents: "auto", cursor: "pointer" }}
            onMouseEnter={e => e.target.style.color = "rgba(255,200,140,.75)"}
            onMouseLeave={e => e.target.style.color = "rgba(255,200,140,.4)"}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Login;